import { formatDtoolsPdfContinuationTitle } from "@shared/dtoolsPdfPackage";
import {
  applyPdfQuotationLogoStyles,
  applyPdfQuotationPageShellStyles,
  PDF_QUOTATION_CONTENT_WIDTH_PX,
  PDF_QUOTATION_MARGIN_Y_PX,
} from "@/lib/proposalPdfQuotationLayout";

/** Content width inside A4 quotation margins (~180mm). */
export const PDF_PRINT_PAGE_WIDTH_PX = PDF_QUOTATION_CONTENT_WIDTH_PX;
/**
 * Body area under repeating chrome. Tuned after fixing schedule-row measurement
 * (thead was previously double-counted per row, causing sparse pages).
 */
export const PDF_PRINT_PAGE_BODY_HEIGHT_PX = 960;
/** Vertical gap between pagination units in the page body. */
export const PDF_PRINT_PAGE_BODY_GAP_PX = 8;

type PaginationRestore = {
  source: HTMLElement;
  pagesHost: HTMLElement;
};

function measureHeight(element: HTMLElement, measureHost: HTMLElement, widthPx: number): number {
  measureHost.style.width = `${widthPx}px`;
  measureHost.innerHTML = "";
  const clone = element.cloneNode(true) as HTMLElement;
  measureHost.appendChild(clone);
  return clone.getBoundingClientRect().height;
}

/**
 * Schedule row units embed a full mini-table (thead + row) for keep-together.
 * When packing consecutive rows, only the first unit should charge for heading/thead —
 * later units measure the row alone so pages fill correctly.
 */
function measurePaginationUnitHeight(
  unit: HTMLElement,
  measureHost: HTMLElement,
  widthPx: number,
  priorScheduleInBucket: boolean,
): number {
  if (unit.dataset.pdfScheduleRowUnit !== "true") {
    return measureHeight(unit, measureHost, widthPx);
  }

  if (!priorScheduleInBucket) {
    return measureHeight(unit, measureHost, widthPx);
  }

  const row = unit.querySelector("tbody > tr");
  if (!(row instanceof HTMLElement)) {
    return measureHeight(unit, measureHost, widthPx);
  }

  const table = unit.querySelector("table");
  const probe = document.createElement("table");
  if (table) {
    probe.className = table.className;
    const style = table.getAttribute("style");
    if (style) probe.setAttribute("style", style);
    const colgroup = table.querySelector("colgroup");
    if (colgroup) probe.appendChild(colgroup.cloneNode(true));
  }
  const tbody = document.createElement("tbody");
  tbody.appendChild(row.cloneNode(true));
  probe.appendChild(tbody);
  return measureHeight(probe, measureHost, widthPx);
}

function isScheduleTable(element: HTMLElement): boolean {
  return element.dataset.pdfScheduleTable === "true" || element.matches("[data-pdf-schedule-table]");
}

function isSplittableContainer(element: HTMLElement): boolean {
  if (element.classList.contains("pdf-keep-together")) return false;
  if (element.dataset.pdfPaginationUnit === "true") return true;
  if (element.matches("[data-pdf-primary-components]")) return true;
  if (element.matches("[data-testid='dtools-option-equipment-view']")) return true;
  return false;
}

function createScheduleRowUnit(
  table: HTMLTableElement,
  thead: HTMLElement | null,
  colgroup: HTMLElement | null,
  row: HTMLElement,
  heading: HTMLElement | null,
  includeHeading: boolean,
): HTMLElement {
  const unit = document.createElement("div");
  unit.dataset.pdfScheduleRowUnit = "true";
  unit.className = "pdf-keep-together";

  if (includeHeading && heading) {
    unit.appendChild(heading.cloneNode(true));
  }

  const mini = document.createElement("table");
  mini.className = table.className;
  const tableStyle = table.getAttribute("style");
  if (tableStyle) mini.setAttribute("style", tableStyle);
  if (colgroup) mini.appendChild(colgroup.cloneNode(true));
  if (thead) mini.appendChild(thead.cloneNode(true));
  const tbody = document.createElement("tbody");
  tbody.appendChild(row.cloneNode(true));
  mini.appendChild(tbody);
  unit.appendChild(mini);
  return unit;
}

function collectPaginationUnits(root: HTMLElement): HTMLElement[] {
  const units: HTMLElement[] = [];

  const walk = (node: HTMLElement) => {
    if (node.dataset.pdfSummaryHeading !== undefined) return;
    if (isScheduleTable(node)) {
      const table = (node.matches("table") ? node : node.querySelector("table")) as HTMLTableElement | null;
      if (!table) {
        units.push(node);
        return;
      }
      const thead = table.querySelector("thead");
      const colgroup = table.querySelector("colgroup");
      const heading = node.querySelector<HTMLElement>("[data-pdf-schedule-heading]");
      const rows = Array.from(table.querySelectorAll("tbody > tr")).filter(
        (row): row is HTMLElement => row instanceof HTMLElement,
      );
      if (rows.length === 0) {
        units.push(node);
        return;
      }
      rows.forEach((row, index) => {
        units.push(createScheduleRowUnit(table, thead, colgroup, row, heading, index === 0));
      });
      return;
    }
    if (node.classList.contains("pdf-keep-together")) {
      units.push(node);
      return;
    }
    if (isSplittableContainer(node)) {
      const children = Array.from(node.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
      );
      if (children.length === 0) {
        units.push(node);
        return;
      }
      for (const child of children) walk(child);
      return;
    }
    if (node.children.length === 0) {
      if (node.textContent?.trim() || node.querySelector("img,svg,table")) units.push(node);
      return;
    }
    for (const child of Array.from(node.children)) {
      if (child instanceof HTMLElement) walk(child);
    }
  };

  for (const child of Array.from(root.children)) {
    if (child instanceof HTMLElement) walk(child);
  }
  return units;
}

function appendUnitsToBody(body: HTMLElement, units: HTMLElement[]): void {
  let scheduleBuffer: HTMLElement[] = [];

  const flushSchedule = () => {
    if (scheduleBuffer.length === 0) return;
    const first = scheduleBuffer[0];
    const firstTable = first.querySelector("table");
    if (!firstTable) {
      for (const unit of scheduleBuffer) body.appendChild(unit);
      scheduleBuffer = [];
      return;
    }

    const wrapper = document.createElement("div");
    wrapper.dataset.pdfScheduleTable = "true";
    const heading = first.querySelector("[data-pdf-schedule-heading]");
    if (heading) wrapper.appendChild(heading.cloneNode(true));

    const outTable = document.createElement("table");
    outTable.className = firstTable.className;
    const style = firstTable.getAttribute("style");
    if (style) outTable.setAttribute("style", style);
    const colgroup = firstTable.querySelector("colgroup");
    if (colgroup) outTable.appendChild(colgroup.cloneNode(true));
    const thead = firstTable.querySelector("thead");
    if (thead) outTable.appendChild(thead.cloneNode(true));
    const outBody = document.createElement("tbody");
    for (const unit of scheduleBuffer) {
      const row = unit.querySelector("tbody > tr");
      if (row) outBody.appendChild(row.cloneNode(true));
    }
    outTable.appendChild(outBody);
    wrapper.appendChild(outTable);
    body.appendChild(wrapper);
    scheduleBuffer = [];
  };

  for (const unit of units) {
    if (unit.dataset.pdfScheduleRowUnit === "true") {
      scheduleBuffer.push(unit);
    } else {
      flushSchedule();
      body.appendChild(unit);
    }
  }
  flushSchedule();
}

function applyContinuationTitle(chrome: HTMLElement, sectionTitle: string): void {
  const titleEl = chrome.querySelector<HTMLElement>('[data-testid="dtools-pdf-section-title"]');
  if (titleEl) {
    titleEl.textContent = formatDtoolsPdfContinuationTitle(titleEl.textContent?.trim() || sectionTitle);
    return;
  }
  const continued = document.createElement("h2");
  continued.dataset.testid = "dtools-pdf-section-title";
  continued.className = "text-[14px] font-semibold uppercase tracking-[0.08em] text-[#1A2744]";
  continued.textContent = formatDtoolsPdfContinuationTitle(sectionTitle);
  chrome.appendChild(continued);
}

function createPrintPage(
  title: string,
  logoUrl: string,
  units: HTMLElement[],
  chrome: HTMLElement | null,
  isContinuation: boolean,
): HTMLElement {
  const page = document.createElement("div");
  applyPdfQuotationPageShellStyles(page);

  if (chrome) {
    const chromeClone = chrome.cloneNode(true) as HTMLElement;
    if (isContinuation) applyContinuationTitle(chromeClone, title);
    for (const logo of Array.from(chromeClone.querySelectorAll<HTMLImageElement>("img"))) {
      applyPdfQuotationLogoStyles(logo);
    }
    page.appendChild(chromeClone);
  } else {
    const header = document.createElement("div");
    header.className = "pdf-print-page-header";
    header.style.display = "flex";
    header.style.alignItems = "flex-start";
    header.style.justifyContent = "space-between";
    header.style.gap = "12px";
    header.style.marginBottom = `${Math.round(PDF_QUOTATION_MARGIN_Y_PX / 2)}px`;
    header.innerHTML = `
      <p class="pdf-print-page-title"></p>
      <img class="pdf-print-page-logo" alt="Clarity" />
    `;
    const titleEl = header.querySelector(".pdf-print-page-title") as HTMLElement | null;
    const logoEl = header.querySelector(".pdf-print-page-logo") as HTMLImageElement | null;
    if (titleEl) {
      titleEl.style.margin = "0";
      titleEl.style.fontSize = "11pt";
      titleEl.style.fontWeight = "600";
      titleEl.style.lineHeight = "1.3";
      titleEl.style.color = "#1A2744";
      titleEl.textContent = isContinuation ? formatDtoolsPdfContinuationTitle(title) : title;
    }
    if (logoEl) {
      if (logoUrl) logoEl.src = logoUrl;
      applyPdfQuotationLogoStyles(logoEl);
    }
    page.appendChild(header);
  }

  const body = document.createElement("div");
  body.className = "pdf-print-page-body";
  body.style.display = "flex";
  body.style.flexDirection = "column";
  body.style.gap = `${PDF_PRINT_PAGE_BODY_GAP_PX}px`;
  body.style.marginTop = chrome ? "10px" : "0";
  appendUnitsToBody(body, units);
  page.appendChild(body);
  return page;
}

function paginateSection(
  source: HTMLElement,
  title: string,
  logoUrl: string,
  measureHost: HTMLElement,
): HTMLElement[] {
  const content = source.querySelector<HTMLElement>("[data-pdf-pagination-content]");
  if (!content) return [];

  const chrome = source.querySelector<HTMLElement>("[data-pdf-section-chrome]");
  const units = collectPaginationUnits(content).map((unit) => unit.cloneNode(true) as HTMLElement);
  if (units.length === 0 && chrome) {
    return [createPrintPage(title, logoUrl, [], chrome, false)];
  }
  if (units.length === 0) return [];

  const pages: HTMLElement[] = [];
  let bucket: HTMLElement[] = [];
  let bucketHeight = 0;
  let isFirstPage = true;

  const flush = () => {
    if (bucket.length === 0) return;
    pages.push(createPrintPage(title, logoUrl, bucket, chrome, !isFirstPage));
    bucket = [];
    bucketHeight = 0;
    isFirstPage = false;
  };

  for (const unit of units) {
    const priorScheduleInBucket =
      bucket.length > 0 &&
      unit.dataset.pdfScheduleRowUnit === "true" &&
      bucket[bucket.length - 1]?.dataset.pdfScheduleRowUnit === "true";
    const unitHeight = measurePaginationUnitHeight(
      unit,
      measureHost,
      PDF_PRINT_PAGE_WIDTH_PX,
      priorScheduleInBucket,
    );
    const gap = bucket.length > 0 ? PDF_PRINT_PAGE_BODY_GAP_PX : 0;

    if (unitHeight > PDF_PRINT_PAGE_BODY_HEIGHT_PX) {
      flush();
      pages.push(createPrintPage(title, logoUrl, [unit], chrome, !isFirstPage));
      isFirstPage = false;
      continue;
    }
    if (bucketHeight > 0 && bucketHeight + gap + unitHeight > PDF_PRINT_PAGE_BODY_HEIGHT_PX) {
      flush();
    }
    const appliedGap = bucket.length > 0 ? PDF_PRINT_PAGE_BODY_GAP_PX : 0;
    bucket.push(unit);
    bucketHeight += appliedGap + unitHeight;
  }
  flush();
  return pages;
}

export function paginateDtoolsPdfPackage(root: HTMLElement): () => void {
  if (root.dataset.pdfPaginationActive === "true") {
    return () => restoreDtoolsPdfPackagePagination(root);
  }

  const logoUrl = root.dataset.pdfLogoUrl?.trim() ?? "";
  const measureHost = document.createElement("div");
  measureHost.className = "dtools-pdf-pagination-measure-host";
  measureHost.style.cssText =
    "position:fixed;left:-20000px;top:0;visibility:hidden;pointer-events:none;";
  document.body.appendChild(measureHost);

  const pagesHost = document.createElement("div");
  pagesHost.dataset.pdfPaginationPages = "true";
  const restores: PaginationRestore[] = [];

  const sectionSelector =
    '[data-testid="dtools-pdf-summary-section"], [data-testid^="dtools-pdf-option-"], [data-testid="dtools-pdf-terms-section"]';
  for (const source of Array.from(root.querySelectorAll<HTMLElement>(sectionSelector))) {
    const title = source.dataset.pdfPaginationTitle?.trim() ?? "";
    if (!title) continue;
    const pages = paginateSection(source, title, logoUrl, measureHost);
    if (pages.length === 0) continue;
    source.classList.add("pdf-pagination-source-hidden");
    source.setAttribute("aria-hidden", "true");
    for (const page of pages) pagesHost.appendChild(page);
    restores.push({ source, pagesHost });
  }

  const paginatedPages = pagesHost.querySelectorAll<HTMLElement>(".pdf-print-page");
  if (paginatedPages.length > 0) {
    paginatedPages[paginatedPages.length - 1]?.classList.add("pdf-print-page-last");
  }

  const closing = root.querySelector<HTMLElement>('[data-testid="dtools-pdf-closing-page"]');
  root.insertBefore(pagesHost, closing);

  root.dataset.pdfPaginationActive = "true";
  document.body.removeChild(measureHost);

  return () => restoreDtoolsPdfPackagePagination(root);
}

export function restoreDtoolsPdfPackagePagination(root: HTMLElement): void {
  root.querySelector('[data-pdf-pagination-pages="true"]')?.remove();
  for (const source of Array.from(root.querySelectorAll<HTMLElement>(".pdf-pagination-source-hidden"))) {
    source.classList.remove("pdf-pagination-source-hidden");
    source.removeAttribute("aria-hidden");
  }
  delete root.dataset.pdfPaginationActive;
}
